import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { Add, Delete, ArrowBack } from '@mui/icons-material';
import materialRequisitionService from '../services/materialRequisitionService';
import productService from '../services/productService';
import fileService from '../services/fileService';
import FileAttachmentManager from './FileAttachmentManager';
import MaterialIssueDialog from './workOrder/MaterialIssueDialog';

/**
 * Material Requisition Form Component
 * 
 * Allows users to create a new material requisition with multiple products.
 * Features:
 * - Dynamic add/remove product rows
 * - Product search using Autocomplete
 * - Quantity validation
 * - Form validation before submit
 * - Navigation to list page after success
 * - Attachment upload support
 */
const MaterialRequisitionForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  // Edit mode state
  const [existingRequisition, setExistingRequisition] = useState(null);
  const [pageLoading, setPageLoading] = useState(isEdit);
  const [pageError, setPageError] = useState(null);

  // Create mode state
  const [products, setProducts] = useState([]);
  const [notes, setNotes] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [productSearchTerms, setProductSearchTerms] = useState({});
  const [productSearchResults, setProductSearchResults] = useState({});
  const [productSearchLoading, setProductSearchLoading] = useState({});

  // Edit mode: load existing requisition and reuse MaterialIssueDialog UI
  useEffect(() => {
    if (!isEdit) return;
    const loadRequisition = async () => {
      try {
        const data = await materialRequisitionService.getRequisition(id);
        setExistingRequisition(data);
      } catch (err) {
        console.error('Failed to load requisition:', err);
        setPageError(err.response?.data?.error || err.response?.data?.message || '加载领料申请失败');
      } finally {
        setPageLoading(false);
      }
    };
    loadRequisition();
  }, [isEdit, id]);

  const handleEditSubmit = useCallback(async (payload) => {
    return await materialRequisitionService.updateRequisition(id, payload);
  }, [id]);

  /**
   * Initialize with one empty product row when creating new
   */
  useEffect(() => {
    if (!isEdit && products.length === 0) {
      handleAddProduct();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  /**
   * Add a new product row
   */
  const handleAddProduct = () => {
    const newId = Date.now().toString();
    setProducts(prev => [...prev, {
      id: newId,
      _id: '',              // MongoDB ObjectId of the product
      product_id: '',       // Product code (string)
      product_name: '',
      quantity_requested: 1,
      notes: ''
    }]);
    setProductSearchTerms(prev => ({ ...prev, [newId]: '' }));
    setProductSearchResults(prev => ({ ...prev, [newId]: [] }));
  };

  /**
   * Remove a product row
   */
  const handleRemoveProduct = (id) => {
    setProducts(prev => prev.filter(p => p.id !== id));
    setProductSearchTerms(prev => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    setProductSearchResults(prev => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  /**
   * Update product field value
   */
  const handleProductChange = (id, field, value) => {
    setProducts(prev => prev.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  /**
   * Search products for Autocomplete
   */
  const searchProducts = useCallback(async (searchTerm, rowId) => {
    if (!searchTerm || searchTerm.trim().length < 1) {
      setProductSearchResults(prev => ({ ...prev, [rowId]: [] }));
      return;
    }

    setProductSearchLoading(prev => ({ ...prev, [rowId]: true }));
    
    try {
      const response = await productService.searchProducts(searchTerm, 1, 20);
      setProductSearchResults(prev => ({ 
        ...prev, 
        [rowId]: response.products || [] 
      }));
    } catch (err) {
      console.error('Failed to search products:', err);
      setProductSearchResults(prev => ({ ...prev, [rowId]: [] }));
    } finally {
      setProductSearchLoading(prev => ({ ...prev, [rowId]: false }));
    }
  }, []);

  /**
   * Debounced search effect
   */
  useEffect(() => {
    const timers = {};
    
    Object.entries(productSearchTerms).forEach(([rowId, term]) => {
      if (term && term.length >= 1) {
        timers[rowId] = setTimeout(() => {
          searchProducts(term, rowId);
        }, 300);
      }
    });

    return () => {
      Object.values(timers).forEach(timer => clearTimeout(timer));
    };
  }, [productSearchTerms, searchProducts]);

  /**
   * Handle product selection from Autocomplete
   * 
   * Data structure mapping:
   * - _id: MongoDB ObjectId of the product (for database reference)
   * - product_id: Product code string (for business logic and display)
   */
  const handleProductSelect = (rowId, product) => {
    if (product) {
      setProducts(prev => prev.map(p =>
        p.id === rowId ? {
          ...p,
          _id: product._id,
          product_id: product.product_id || product.product_code,
          product_name: product.name,
          unit: product.unit || '个'
        } : p
      ));
    }
  };

  /**
   * Validate form before submission
   */
  const validateForm = () => {
    if (products.length === 0) {
      setError('至少需要添加一个物料');
      return false;
    }

    for (const product of products) {
      if (!product._id) {
        setError('请选择所有物料');
        return false;
      }
      if (!product.quantity_requested || product.quantity_requested < 1) {
        setError('数量必须大于0');
        return false;
      }
    }

    return true;
  };


  /**
   * Submit form
   */
  const handleSubmit = async () => {
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        products: products.map(p => ({
          _id: p._id,                    // MongoDB ObjectId of the product
          product_id: p.product_id,      // Product code (string)
          quantity_requested: parseInt(p.quantity_requested, 10),
          notes: p.notes || ''
        })),
        notes
      };

      let requisitionId = id;

      if (isEdit) {
        // Update existing requisition
        await materialRequisitionService.updateRequisition(id, payload);
      } else {
        // Create new requisition
        const response = await materialRequisitionService.createRequisition({
          ...payload,
          attachments: []
        });
        requisitionId = response._id || response.id;
      }

      // Upload any files that are stored locally
      if (attachments.length > 0 && requisitionId && !isEdit) {
        const attachmentIds = [];

        for (const file of attachments) {
          // Upload files with the requisition ID
          if (file.file && file.isLocal) {
            try {
              const uploadResult = await fileService.uploadFile(
                file.file,
                'material_requisitions',
                requisitionId,
                {
                  description: file.description || '',
                  tags: file.tags || []
                }
              );
              if (uploadResult.fileId || uploadResult.file_id) {
                attachmentIds.push(uploadResult.fileId || uploadResult.file_id);
              }
            } catch (uploadErr) {
              console.error('Failed to upload file:', file.filename, uploadErr);
              // Continue with other files even if one fails
            }
          }
        }

        // If there are new attachments, update the requisition
        if (attachmentIds.length > 0) {
          await materialRequisitionService.updateRequisition(requisitionId, {
            attachments: attachmentIds
          });
        }
      }

      // Navigate to list page after successful save
      navigate('/material-requisitions');
    } catch (err) {
      console.error(`Failed to ${isEdit ? 'update' : 'create'} requisition:`, err);
      setError(err.response?.data?.error || err.response?.data?.message || `${isEdit ? '更新' : '创建'}领料申请失败`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle cancel button click
   */
  const handleCancel = () => {
    navigate('/material-requisitions');
  };

  // Edit mode: reuse MaterialIssueDialog UI
  if (isEdit) {
    if (pageLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (pageError) {
      return (
        <Paper sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
          <Alert severity="error">{pageError}</Alert>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={handleCancel}>
              返回列表
            </Button>
          </Box>
        </Paper>
      );
    }

    return (
      <MaterialIssueDialog
        open={true}
        onClose={handleCancel}
        workOrder={null}
        mode="edit"
        existingRequisition={existingRequisition}
        onSubmit={handleEditSubmit}
        onSuccess={handleCancel}
      />
    );
  }

  return (
    <Paper sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleCancel} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" component="h1">
          {isEdit ? '编辑领料申请' : '新建领料申请'}
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Products Table */}
      <TableContainer sx={{ mb: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '35%' }}>物料</TableCell>
              <TableCell sx={{ width: '15%' }}>数量</TableCell>
              <TableCell sx={{ width: '10%' }}>单位</TableCell>
              <TableCell sx={{ width: '30%' }}>备注</TableCell>
              <TableCell sx={{ width: '10%' }}>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id}>
                <TableCell>
                  <Autocomplete
                    value={productSearchResults[product.id]?.find(p =>
                      (product._id && p._id === product._id) ||
                      (product.product_id && (p.product_id === product.product_id || p.product_code === product.product_id))
                    ) || null}
                    inputValue={productSearchTerms[product.id] || ''}
                    onInputChange={(event, newInputValue) => {
                      setProductSearchTerms(prev => ({
                        ...prev,
                        [product.id]: newInputValue
                      }));
                    }}
                    onChange={(event, newValue) => {
                      handleProductSelect(product.id, newValue);
                    }}
                    options={productSearchResults[product.id] || []}
                    getOptionLabel={(option) =>
                      `${option.product_id || option.product_code} - ${option.name}`
                    }
                    isOptionEqualToValue={(option, value) =>
                      option._id === value._id ||
                      option.product_id === value.product_id ||
                      option.product_code === value.product_id
                    }
                    loading={productSearchLoading[product.id]}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="搜索物料编号或名称..."
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {productSearchLoading[product.id] && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option._id}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {option.product_id || option.product_code}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.name}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    noOptionsText="未找到物料"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    value={product.quantity_requested}
                    onChange={(e) => handleProductChange(
                      product.id, 
                      'quantity_requested', 
                      parseInt(e.target.value, 10) || 1
                    )}
                    inputProps={{ min: 1 }}
                    sx={{ width: '100%' }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {product.unit || '个'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    value={product.notes}
                    onChange={(e) => handleProductChange(product.id, 'notes', e.target.value)}
                    placeholder="备注"
                    sx={{ width: '100%' }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton 
                    onClick={() => handleRemoveProduct(product.id)}
                    disabled={products.length <= 1}
                    color="error"
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Product Button */}
      <Button
        startIcon={<Add />}
        onClick={handleAddProduct}
        variant="outlined"
      >
      </Button>

      {/* File Attachments Section */}
      <Box sx={{ mt: 3, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          附件
        </Typography>
        <FileAttachmentManager
          entityType="material_requisitions"
          entityId={null}
          files={attachments}
          onChange={setAttachments}
          maxFiles={5}
          maxSizeMB={10}
          showSearch={false}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Notes Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          申请备注
        </Typography>
        <TextField
          multiline
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="填写申请备注信息..."
          sx={{ width: '100%' }}
        />
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={loading}
        >
          取消
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || products.length === 0}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
              提交中...
            </>
          ) : (
            isEdit ? '保存修改' : '提交申请'
          )}
        </Button>
      </Box>
    </Paper>
  );
};

export default MaterialRequisitionForm;